import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { ensureUrl } from "@/lib/url";
import { toast } from "sonner";

/**
 * Blank vendor form template — a single source of truth so the caller
 * doesn't drift between "new vendor" and "reset after save".
 */
export const BLANK_VENDOR = {
  name: "", type: "Grocery", address: "", phone: "", website: "",
  online_order_url: "", search_url_template: "", distance_km: "", categories: [],
  hours: { mon: "", tue: "", wed: "", thu: "", fri: "", sat: "", sun: "" },
  notes: "",
};

/**
 * useVendorsData — owns the vendor list, form state, and CRUD handlers.
 * Gas suppliers are filtered out because they live on the /gas-bottles
 * page and would otherwise clutter this table.
 */
export default function useVendorsData() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK_VENDOR);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors");
      setItems(data);
    } catch (e) {
      console.error("Failed to load vendors", e);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visibleItems = useMemo(
    () => items.filter((v) => v.type !== "Gas supplier"),
    [items],
  );

  const toggleCat = useCallback((c) => {
    setForm((f) => ({
      ...f,
      categories: f.categories.includes(c)
        ? f.categories.filter((x) => x !== c)
        : [...f.categories, c],
    }));
  }, []);

  const save = useCallback(async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    try {
      const payload = {
        ...form,
        website: ensureUrl(form.website),
        online_order_url: ensureUrl(form.online_order_url),
        search_url_template: (form.search_url_template || "").trim() || null,
        distance_km: form.distance_km ? parseFloat(form.distance_km) : null,
      };
      if (editId) await api.put(`/vendors/${editId}`, payload);
      else await api.post("/vendors", payload);
      setOpen(false); setEditId(null); setForm(BLANK_VENDOR);
      toast.success("Saved");
      load();
      return true;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      return false;
    }
  }, [form, editId, load]);

  const startEdit = useCallback((it) => {
    setForm({
      ...BLANK_VENDOR, ...it,
      hours: { ...BLANK_VENDOR.hours, ...(it.hours || {}) },
      distance_km: it.distance_km ?? "",
      categories: it.categories || [],
    });
    setEditId(it.id);
    setOpen(true);
  }, []);

  const startCreate = useCallback(() => {
    setEditId(null);
    setForm(BLANK_VENDOR);
    setOpen(true);
  }, []);

  const remove = useCallback(async (id) => {
    await api.delete(`/vendors/${id}`);
    load();
  }, [load]);

  return {
    // list
    items, visibleItems,
    // form dialog
    open, setOpen,
    editId,
    form, setForm,
    // handlers
    toggleCat, save, startEdit, startCreate, remove,
    // reloader
    load,
  };
}
